import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { patchWindowStyles } from './colorSanitizer';

async function convertImgToBase64(src: string): Promise<string> {
  if (!src || src.startsWith('data:')) return src;
  try {
    const proxyUrl = `/api/image-proxy?url=${encodeURIComponent(src)}&format=json`;
    const res = await fetch(proxyUrl);
    if (res.ok) {
      const json = await res.json();
      if (json.success && json.base64) return json.base64;
    }
  } catch {}
  return src;
}

export async function exportTechnicalSheetToPdf(
  elementId: string,
  fileName: string
): Promise<boolean> {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error('No se encontró el contenedor de la ficha técnica.');
  }

  // Pre-cargar imágenes para evitar demoras repetidas
  const images = Array.from(element.querySelectorAll('img'));
  await Promise.all(
    images.map(async (img) => {
      const src = img.getAttribute('src');
      if (src && !src.startsWith('data:')) {
        const b64 = await convertImgToBase64(src);
        if (b64 && b64.startsWith('data:')) {
          img.setAttribute('src', b64);
        }
      }
    })
  );

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  const unpatchMain = patchWindowStyles(window);

  try {
    // scale 1.75 ofrece máxima nitidez vectorial en impresión A4 y reduce drásticamente el tiempo de rasterizado
    const canvas = await html2canvas(element, {
      scale: 1.75,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: 850,
      onclone: (clonedDoc, clonedElement) => {
        if (clonedDoc.defaultView) {
          patchWindowStyles(clonedDoc.defaultView);
        }
        const style = clonedDoc.createElement('style');
        style.textContent = `
          img {
            display: inline-block !important;
            vertical-align: baseline !important;
          }
          *, *::before, *::after {
            letter-spacing: normal !important;
            word-spacing: normal !important;
            box-sizing: border-box !important;
          }
          body, p, span, h1, h2, h3, h4, div, a, td, th {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
          }
        `;
        clonedDoc.head.appendChild(style);

        if (clonedDoc.body) {
          clonedDoc.body.style.backgroundColor = '#ffffff';
          clonedDoc.body.style.color = '#1e293b';
        }
        clonedElement.style.width = '800px';
        clonedElement.style.minWidth = '800px';
        clonedElement.style.maxWidth = '800px';
        clonedElement.style.margin = '0 auto';
        clonedElement.style.boxSizing = 'border-box';
      },
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const marginMm = 8;
    const printableWidth = pageWidth - marginMm * 2;
    const printableHeight = pageHeight - marginMm * 2;

    let finalWidth = printableWidth;
    let finalHeight = (canvas.height * finalWidth) / canvas.width;

    if (finalHeight > printableHeight) {
      finalHeight = printableHeight;
      finalWidth = (canvas.width * finalHeight) / canvas.height;
    }

    const xOffset = marginMm + (printableWidth - finalWidth) / 2;
    const yOffset = marginMm + (printableHeight - finalHeight) / 2;

    pdf.addImage(imgData, 'JPEG', xOffset, yOffset, finalWidth, finalHeight);
    pdf.save(fileName);
    return true;
  } finally {
    unpatchMain();
  }
}
